import { sanitizeColumnWidths } from '../utils/clientTableColumnWidths.js';
import { sanitizeTableSorting } from '../utils/clientTableSortPreference.js';
import {
  migrateLegacyClientListFilters,
  sanitizeClientListFilters,
} from '../utils/clientListFilters.js';
import { supportAccessRole } from './advisoryRoles';

export const ADVISORY_PORTAL_TAB = 'advisoryPortal';
export const ADVISORY_PORTAL_FILTERS_KEY = 'advisory-portal-filters';

/** Retired GA Portal tab id — migrated to {@link ADVISORY_PORTAL_TAB}. */
export const LEGACY_GA_PORTAL_TAB = 'clients';

/** Retired portal tab ids — migrated to {@link ADVISORY_PORTAL_TAB}. */
export const LEGACY_ADVISORY_PORTAL_TABS = [
  'adviserPortal',
  'ufSupportPortal',
  'arSupportPortal',
  'arPortal',
  'supportPortal',
];

/** Always visible — not shown in column toggles. */
export const ADVISORY_PORTAL_LOCKED_COLUMN_IDS = ['name'];

/** Optional columns — same choices for every advisory portal user. */
export const ADVISORY_PORTAL_TOGGLEABLE_COLUMNS = [
  { id: 'contact', label: 'Contact' },
  { id: 'adviser', label: 'Adviser' },
  { id: 'advisory', label: 'Advisory' },
  { id: 'marketer', label: 'Marketer' },
  { id: 'factFind', label: 'Fact Find' },
  { id: 'presentation', label: 'Presentation' },
  { id: 'welcomeCall', label: 'Welcome Call' },
  { id: 'envelopeSigned', label: 'Envelope Signed' },
  { id: 'nextARDue', label: 'Next AR due' },
  { id: 'arBooked', label: 'AR Booked' },
  { id: 'status', label: 'Open Tasks' },
  { id: 'currentStep', label: 'Open Task' },
  { id: 'assigned', label: 'Assigned' },
];

export const ADVISORY_PORTAL_TOGGLEABLE_COLUMN_IDS = ADVISORY_PORTAL_TOGGLEABLE_COLUMNS.map((c) => c.id);

const VALID_TOGGLEABLE_COLUMN_IDS = new Set(ADVISORY_PORTAL_TOGGLEABLE_COLUMN_IDS);

/** Former locked columns — merged into saved prefs so upgrades stay visible. */
const LEGACY_ALWAYS_VISIBLE_COLUMN_IDS = ['contact', 'currentStep', 'assigned', 'status'];

/** Default optional columns shown on first visit. */
export const DEFAULT_ADVISORY_PORTAL_VISIBLE_OPTIONAL_COLUMNS = [
  'contact',
  'marketer',
  'factFind',
  'presentation',
  'welcomeCall',
  'status',
  'currentStep',
  'assigned',
];

function normalizeUserEmail(userEmail) {
  return (userEmail || '').trim().toLowerCase();
}

function filtersStorageKey(userEmail) {
  const email = normalizeUserEmail(userEmail);
  return email ? `${ADVISORY_PORTAL_FILTERS_KEY}:${email}` : ADVISORY_PORTAL_FILTERS_KEY;
}

function readFiltersRoot(userEmail) {
  if (typeof window === 'undefined') return {};
  const key = filtersStorageKey(userEmail);
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
    if (normalizeUserEmail(userEmail)) {
      const legacyRaw = localStorage.getItem(ADVISORY_PORTAL_FILTERS_KEY);
      if (legacyRaw) {
        const parsed = JSON.parse(legacyRaw);
        writeFiltersRoot(userEmail, parsed);
        return parsed;
      }
    }
    return {};
  } catch (_) {
    return {};
  }
}

function writeFiltersRoot(userEmail, root) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(filtersStorageKey(userEmail), JSON.stringify(root));
  } catch (_) {
    // ignore storage errors
  }
}

/**
 * Retired column ids → their replacement. The 'AR Adviser' column merged into
 * 'Adviser' (Sept 2026: one Assigned Adviser per client), so saved column lists,
 * admin defaults and sorting that still carry `arAdviser` read as `adviser`.
 */
const LEGACY_COLUMN_ID_ALIASES = { arAdviser: 'adviser' };

export function mapLegacyAdvisoryColumnId(id) {
  return Object.prototype.hasOwnProperty.call(LEGACY_COLUMN_ID_ALIASES, id) ? LEGACY_COLUMN_ID_ALIASES[id] : id;
}

function sanitizeVisibleColumnIds(columnIds) {
  if (!Array.isArray(columnIds)) return null;
  return [...new Set(columnIds.map(mapLegacyAdvisoryColumnId).filter((id) => VALID_TOGGLEABLE_COLUMN_IDS.has(id)))];
}

export function loadAdvisoryPortalVisibleColumns(userEmail) {
  const root = readFiltersRoot(userEmail);
  if (!Array.isArray(root.visibleColumnIds)) {
    return [...DEFAULT_ADVISORY_PORTAL_VISIBLE_OPTIONAL_COLUMNS];
  }
  const saved = sanitizeVisibleColumnIds(root.visibleColumnIds);
  if (saved.length === 0) {
    return [];
  }
  const savedTaskColumns = saved.filter((id) => LEGACY_ALWAYS_VISIBLE_COLUMN_IDS.includes(id));
  const merged = savedTaskColumns.length > 0
    ? saved
    : [...new Set([...saved, ...LEGACY_ALWAYS_VISIBLE_COLUMN_IDS])];
  return ADVISORY_PORTAL_TOGGLEABLE_COLUMN_IDS.filter((id) => merged.includes(id));
}

export function saveAdvisoryPortalVisibleColumns(userEmail, visibleColumnIds) {
  const root = readFiltersRoot(userEmail);
  writeFiltersRoot(userEmail, {
    ...root,
    visibleColumnIds: sanitizeVisibleColumnIds(visibleColumnIds) || [],
  });
}

export function loadAdvisoryPortalColumnWidths(userEmail) {
  return sanitizeColumnWidths(readFiltersRoot(userEmail).columnWidths);
}

export function saveAdvisoryPortalColumnWidths(userEmail, columnWidths) {
  const root = readFiltersRoot(userEmail);
  writeFiltersRoot(userEmail, {
    ...root,
    columnWidths: sanitizeColumnWidths(columnWidths),
  });
}

export function loadAdvisoryPortalSorting(userEmail) {
  return sanitizeTableSorting(readFiltersRoot(userEmail).sorting)
    .map((entry) => ({ ...entry, id: mapLegacyAdvisoryColumnId(entry.id) }));
}

export function saveAdvisoryPortalSorting(userEmail, sorting) {
  const root = readFiltersRoot(userEmail);
  writeFiltersRoot(userEmail, {
    ...root,
    sorting: sanitizeTableSorting(sorting),
  });
}

export function loadAdvisoryPortalMarketingFilter(userEmail) {
  const root = readFiltersRoot(userEmail);
  if (root.marketingFilter === undefined || root.marketingFilter === null || root.marketingFilter === '') {
    return 'all';
  }
  return String(root.marketingFilter);
}

export function saveAdvisoryPortalMarketingFilter(userEmail, marketingFilter) {
  const root = readFiltersRoot(userEmail);
  writeFiltersRoot(userEmail, {
    ...root,
    marketingFilter: !marketingFilter || marketingFilter === 'all' ? '' : String(marketingFilter),
  });
}

/** Ordered column ids for the table: locked + visible optional columns. */
export function buildAdvisoryPortalColumnOrder(visibleOptionalColumnIds) {
  const optional = ADVISORY_PORTAL_TOGGLEABLE_COLUMN_IDS.filter((id) =>
    visibleOptionalColumnIds.includes(id)
  );
  return ['name', ...optional];
}

export function saveAdvisoryPortalOpenTaskFilters(userEmail, variantKey, selectedOpenTaskTemplateIds) {
  const root = readFiltersRoot(userEmail);
  writeFiltersRoot(userEmail, {
    ...root,
    [variantKey]: {
      ...(root[variantKey] || {}),
      selectedOpenTaskTemplateIds: Array.isArray(selectedOpenTaskTemplateIds)
        ? [...new Set(selectedOpenTaskTemplateIds)]
        : [],
    },
  });
}

export function loadAdvisoryPortalOpenTaskFilters(userEmail, variantKey, validTemplateIds = []) {
  const valid = new Set(validTemplateIds);
  if (typeof window !== 'undefined') {
    try {
      const parsed = readFiltersRoot(userEmail);
      const saved = parsed?.[variantKey]?.selectedOpenTaskTemplateIds;
      if (Array.isArray(saved)) {
        return [...new Set(saved.filter((id) => valid.has(id)))];
      }
    } catch (_) {
      // ignore
    }
  }
  return [];
}

export function saveAdvisoryPortalShowArchived(userEmail, variantKey, showArchived) {
  const root = readFiltersRoot(userEmail);
  writeFiltersRoot(userEmail, {
    ...root,
    [variantKey]: {
      ...(root[variantKey] || {}),
      showArchived: showArchived === true,
    },
  });
}

export function loadAdvisoryPortalShowArchived(userEmail, variantKey) {
  if (typeof window === 'undefined') return false;
  try {
    const parsed = readFiltersRoot(userEmail);
    return parsed?.[variantKey]?.showArchived === true;
  } catch (_) {
    return false;
  }
}

// ---------------------------------------------------------------------------
// v2 client-list filter state (src/utils/clientListFilters.js)
// ---------------------------------------------------------------------------

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Stored v2 filter slot for a variant. Absent → migrate the legacy keys
 * (selectedOpenTaskTemplateIds / marketingFilter / showArchived) on the fly.
 * A slot written before the DNQ and unreleased-AR buckets moved into the modal
 * carries no bucket keys, so those seed from the old header-toggle keys once.
 */
export function loadAdvisoryPortalClientListFilters(userEmail, variantKey) {
  const root = readFiltersRoot(userEmail);
  const variantRoot = root?.[variantKey] || {};
  const buckets = Object.fromEntries(
    Object.entries({ showDnq: variantRoot.showDnq, showUnreleasedAr: variantRoot.showUnreleasedAr })
      .filter(([, value]) => value !== undefined),
  );
  if (isPlainObject(variantRoot.clientListFilters)) {
    return sanitizeClientListFilters({ ...buckets, ...variantRoot.clientListFilters });
  }
  return migrateLegacyClientListFilters({
    selectedOpenTaskTemplateIds: variantRoot.selectedOpenTaskTemplateIds,
    marketingFilter: root?.marketingFilter,
    showArchived: variantRoot.showArchived,
    ...buckets,
  });
}

export function saveAdvisoryPortalClientListFilters(userEmail, variantKey, filters) {
  const root = readFiltersRoot(userEmail);
  writeFiltersRoot(userEmail, {
    ...root,
    [variantKey]: {
      ...(root[variantKey] || {}),
      clientListFilters: sanitizeClientListFilters(filters),
    },
  });
}

export function loadAdvisoryPortalActiveViewId(userEmail, variantKey) {
  const root = readFiltersRoot(userEmail);
  const id = root?.[variantKey]?.activeViewId;
  return typeof id === 'string' && id ? id : null;
}

export function saveAdvisoryPortalActiveViewId(userEmail, variantKey, viewId) {
  const root = readFiltersRoot(userEmail);
  writeFiltersRoot(userEmail, {
    ...root,
    [variantKey]: {
      ...(root[variantKey] || {}),
      activeViewId: typeof viewId === 'string' && viewId ? viewId : null,
    },
  });
}

function hasSavedVariantFilters(variantRoot) {
  return isPlainObject(variantRoot?.clientListFilters) || Array.isArray(variantRoot?.selectedOpenTaskTemplateIds);
}

/** Default filters from an admin group: v2 `filters` when present, else the v1 projection. */
export function defaultFiltersFromGroup(groupDefaults) {
  if (!groupDefaults || typeof groupDefaults !== 'object') return null;
  if (isPlainObject(groupDefaults.filters)) return sanitizeClientListFilters(groupDefaults.filters);
  return migrateLegacyClientListFilters({
    selectedOpenTaskTemplateIds: groupDefaults.selectedOpenTaskTemplateIds,
    advisoryFilter: groupDefaults.advisoryFilter,
  });
}

/**
 * Apply admin-configured defaults (Admin → Default Filters) for an advisory
 * portal variant group (adviser, management, ufSupport, arSupport, arAdviser).
 *
 * Seeds first-visit filters/columns, and overwrites the user's stored values
 * when the admin pushed defaults with a `forceAppliedAt` stamp this browser
 * has not applied yet. Returns the resolved values when applied, else null.
 * @returns {{ filters: object, visibleColumnIds: string[] } | null}
 */
export function applyAdvisoryPortalDefaults(userEmail, variantKey, groupDefaults) {
  if (!groupDefaults || typeof groupDefaults !== 'object') return null;
  const root = readFiltersRoot(userEmail);
  const variantRoot = root?.[variantKey] || {};
  const forceStamp = groupDefaults.forceAppliedAt || null;
  const hasSavedFilters = hasSavedVariantFilters(variantRoot);
  const hasSavedColumns = Array.isArray(root?.visibleColumnIds);
  const forcePending = !!forceStamp && variantRoot.appliedForceStamp !== forceStamp;
  if (!forcePending && hasSavedFilters && hasSavedColumns) return null;

  const defaultFilters = defaultFiltersFromGroup(groupDefaults);
  const defaultColumns = sanitizeVisibleColumnIds(groupDefaults.visibleColumnIds) || [];

  const next = { ...root };
  next[variantKey] = {
    ...variantRoot,
    appliedForceStamp: forceStamp,
    ...(forcePending || !hasSavedFilters
      ? { clientListFilters: defaultFilters }
      : {}),
  };
  // Empty column defaults mean "not configured" — keep the user's/built-in columns.
  if ((forcePending || !hasSavedColumns) && defaultColumns.length > 0) {
    next.visibleColumnIds = defaultColumns;
  }
  writeFiltersRoot(userEmail, next);
  return {
    filters: loadAdvisoryPortalClientListFilters(userEmail, variantKey),
    visibleColumnIds: loadAdvisoryPortalVisibleColumns(userEmail),
  };
}

/** Storage adapter for useClientListFilters (one per advisory portal variant). */
export function advisoryClientListFilterStorage(variantKey) {
  return {
    defaultsGroupKey: variantKey,
    load: (userEmail) => loadAdvisoryPortalClientListFilters(userEmail, variantKey),
    save: (userEmail, filters) => saveAdvisoryPortalClientListFilters(userEmail, variantKey, filters),
    applyDefaults: (userEmail, groupDefaults) => applyAdvisoryPortalDefaults(userEmail, variantKey, groupDefaults),
  };
}

export function resolveAdvisoryPortalVariant(user) {
  // Lazy require avoids circular import with ../services/userTeamService at load time.
  const userTeamService = require('../services/userTeamService').default;
  const config = userTeamService.getUserConfig(user?.email);
  // A provisional adviser gets the AR Support portal.
  const role = supportAccessRole(config?.advisoryRole);
  if (role === 'ufSupport') return 'ufSupport';
  if (role === 'arSupport') return 'arSupport';
  if (role === 'arAdviser') return 'arAdviser';
  if (role === 'management') return 'management';
  // Compliance officers get the team-scoped management portal if they open it.
  if (role === 'compliance') return 'management';
  return 'adviser';
}

export const ADVISORY_PORTAL_VARIANTS = {
  adviser: {
    clientScope: 'assignedToMe',
    autoArDueTransition: false,
    showChangedCircumstances: false,
    emptyMessage: 'No Client Tasks Assigned',
  },
  management: {
    clientScope: 'advisoryTeam',
    autoArDueTransition: false,
    showChangedCircumstances: false,
    emptyMessage: 'No matching clients',
  },
  ufSupport: {
    clientScope: 'advisoryTeam',
    autoArDueTransition: true,
    showChangedCircumstances: false,
    emptyMessage: 'No clients in support queue',
  },
  arSupport: {
    clientScope: 'advisoryTeam',
    autoArDueTransition: true,
    showChangedCircumstances: true,
    emptyMessage: 'No clients in AR support queue',
  },
  arAdviser: {
    clientScope: 'advisoryTeam',
    autoArDueTransition: false,
    showChangedCircumstances: true,
    emptyMessage: 'No clients in AR workflow',
  },
};

/** Normalize page visibility lists: map retired advisory portal tabs to advisoryPortal. */
export function normalizeAdvisoryPortalPageIds(pages) {
  if (!Array.isArray(pages)) return pages;
  const out = [];
  let includeAdvisoryPortal = false;
  for (const id of pages) {
    if (id === 'implementationChecklist' || id === 'tasks') {
      if (!out.includes('adviceJourney')) out.push('adviceJourney');
      continue;
    }
    if (id === LEGACY_GA_PORTAL_TAB || LEGACY_ADVISORY_PORTAL_TABS.includes(id)) {
      includeAdvisoryPortal = true;
      continue;
    }
    if (!out.includes(id)) out.push(id);
  }
  if (includeAdvisoryPortal && !out.includes(ADVISORY_PORTAL_TAB)) {
    out.push(ADVISORY_PORTAL_TAB);
  }
  return out;
}
